// Страница агента (/agents/:id): миссия, инструкции, модель, пауза, «спросить», запуски, задачи, сообщения, знания.
import { useEffect, useId, useMemo, useState } from 'react'
import { api } from '../api.ts'
import { AgentModels } from '../components/AgentModels.tsx'
import { AgentAvatar } from '../components/AgentAvatar.tsx'
import { DirectionChip, ServiceChip, StatusBadge } from '../components/chips.tsx'
import { Icon } from '../components/Icon.tsx'
import { JobList } from '../components/JobList.tsx'
import { MessageThread } from '../components/MessageThread.tsx'
import { ModelInput } from '../components/ModelInput.tsx'
import { SubtaskProgress } from '../components/TaskCardView.tsx'
import { Badge, Button, Empty, ErrorBox, Field, LiveDot, Loading, Section, Stat, Tabs, cx } from '../components/ui.tsx'
import { docHref, formatChars, unitsForServices, useDocUnits } from '../lib/docs.ts'
import { useAsync, useBusy, useDebounced } from '../lib/hooks.ts'
import { AGENT_KIND_LABELS, AGENT_KIND_TONES, KNOWLEDGE_KIND_LABELS, SCOPE_LABELS, formatUsd, timeAgo } from '../lib/format.ts'
import { Markdown } from '../lib/markdown.tsx'
import { Link, TaskLink, openTask } from '../router.tsx'
import { upsertAgent, useAgentMap, useNow, useReconnect, useStream } from '../store.ts'
import { toast, toastError } from '../toast.ts'
import type { Agent, AgentDetail, Task } from '../types.ts'
import { AgentStatusPill } from './AgentsPage.tsx'

type TabId = 'jobs' | 'tasks' | 'messages' | 'knowledge'
const EFFORTS = ['', 'low', 'medium', 'high', 'xhigh', 'max']

export function AgentPage({ id }: { id: string }) {
  const detail = useAsync((signal) => api.get<AgentDetail>(`/api/agents/${encodeURIComponent(id)}`, { signal }), [id])
  const listAgent = useAgentMap().get(id)
  const refetchSoon = useDebounced(detail.reload, 1000)
  const [tab, setTab] = useState<TabId>('jobs')

  useStream((ev) => {
    if (ev.type === 'agent' && ev.data.id === id) {
      detail.setData((cur) => (cur ? { ...cur, agent: { ...cur.agent, ...ev.data } } : cur))
    } else if (ev.type === 'job' && ev.data.agent_id === id) {
      detail.setData((cur) => {
        if (!cur) return cur
        const i = cur.jobs.findIndex((j) => j.id === ev.data.id)
        const jobs = i === -1 ? [ev.data, ...cur.jobs] : cur.jobs.map((j) => (j.id === ev.data.id ? { ...j, ...ev.data } : j))
        return { ...cur, jobs }
      })
      refetchSoon()
    } else if (ev.type === 'message' && ((ev.data.from_type === 'agent' && ev.data.from_id === id) || (ev.data.to_type === 'agent' && ev.data.to_id === id))) {
      detail.setData((cur) => (cur && !cur.messages.some((m) => m.id === ev.data.id) ? { ...cur, messages: [ev.data, ...cur.messages] } : cur))
    }
  })
  useReconnect(detail.reload)

  if (detail.error && !detail.data) {
    return (
      <div className="page">
        <Link to="/agents" className="crumb inline-block hover:text-fg hover:underline">
          агенты
        </Link>
        <div className="mt-3">
          <ErrorBox error={detail.error} onRetry={detail.reload} />
        </div>
      </div>
    )
  }
  if (!detail.data)
    return (
      <div className="page">
        <Loading label="Загружаю агента…" />
      </div>
    )

  const { agent: a0, jobs, tasks, messages, knowledge } = detail.data
  const agent: Agent = { ...listAgent, ...a0 }

  return (
    <div className="page max-w-[1320px]">
      <div className="crumb break-words">
        <Link to="/agents" className="hover:text-fg hover:underline">
          агенты
        </Link>{' '}
        / {agent.id}
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <AgentAvatar agent={agent} size="xl" />
        {/* min-w: на узком экране кнопка паузы уходит на свою строку, а не сжимает имя до пары букв */}
        <div className="min-w-[12rem] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="h1 min-w-0 [overflow-wrap:anywhere]">{agent.name}</h1>
            <AgentStatusPill agent={agent} />
            <AgentModels agent={agent} />
            <Badge tone={AGENT_KIND_TONES[agent.kind]}>{AGENT_KIND_LABELS[agent.kind]}</Badge>
            {agent.direction_id ? <DirectionChip id={agent.direction_id} /> : null}
            {agent.archived ? <Badge tone="neutral">в архиве</Badge> : null}
          </div>
          <div className="mt-1 text-[14px] break-words text-muted">{agent.title}</div>
          {agent.service_ids.length ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {agent.service_ids.map((s) => (
                <Link key={s} to={`/map?service=${encodeURIComponent(s)}`} className="min-w-0 max-w-full">
                  <ServiceChip id={s} />
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <PauseButton agent={agent} onChange={(x) => detail.setData((cur) => (cur ? { ...cur, agent: x } : cur))} />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Работает сейчас" value={agent.running_jobs ?? jobs.filter((j) => j.status === 'running').length} tone={(agent.running_jobs ?? 0) > 0 ? 'ok' : undefined} />
        <Stat label="В очереди" value={agent.queued_jobs ?? jobs.filter((j) => j.status === 'queued').length} />
        <Stat label="Всего запусков" value={agent.jobs_total ?? jobs.length} />
        <Stat label="Расход всего" value={formatUsd(agent.cost_total_usd ?? jobs.reduce((s, j) => s + j.cost_usd, 0))} />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Tabs<TabId>
            label="Разделы агента"
            value={tab}
            onChange={setTab}
            items={[
              { id: 'jobs', label: 'Запуски', count: jobs.length },
              { id: 'tasks', label: 'Задачи', count: tasks.length },
              { id: 'messages', label: 'Сообщения', count: messages.length },
              { id: 'knowledge', label: 'Знания', count: knowledge.length },
            ]}
          />
          {tab === 'jobs' ? <JobList jobs={[...jobs].sort((x, y) => y.id - x.id)} showAgent={false} showTask /> : null}
          {tab === 'tasks' ? <AgentTasks tasks={tasks} /> : null}
          {tab === 'messages' ? (
            <MessageThread messages={[...messages].sort((x, y) => y.id - x.id)} showTask emptyText="Сообщений нет" />
          ) : null}
          {tab === 'knowledge' ? (
            knowledge.length ? (
              <div className="flex flex-col gap-2">
                {knowledge.map((k) => (
                  <article key={k.id} className="rounded-card border border-line bg-surface px-4 py-3 shadow-card">
                    <div className="flex flex-wrap items-center gap-1.5 text-[13px]">
                      {k.pinned ? (
                        <span title="Закреплено" className="text-muted">
                          <Icon name="pin" size={13} />
                        </span>
                      ) : null}
                      <span className="min-w-0 font-semibold [overflow-wrap:anywhere]">{k.title}</span>
                      <Badge>{KNOWLEDGE_KIND_LABELS[k.kind] ?? k.kind}</Badge>
                      <span className="text-subtle">{SCOPE_LABELS[k.scope_type]}</span>
                    </div>
                    <Markdown text={k.body} className="mt-1 text-[13px] text-fg-2" />
                  </article>
                ))}
              </div>
            ) : (
              <Empty icon="star">Агент ещё ничего не записал в базу знаний</Empty>
            )
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <AskAgent agent={agent} />
          <AgentDocs agent={agent} />
          <AgentSettings
            key={agent.id}
            agent={agent}
            onSaved={(x) => detail.setData((cur) => (cur ? { ...cur, agent: { ...cur.agent, ...x } } : cur))}
          />
        </div>
      </div>
    </div>
  )
}

function PauseButton({ agent, onChange }: { agent: Agent; onChange: (a: Agent) => void }) {
  const [busy, run] = useBusy()
  const paused = agent.status === 'paused'
  const toggle = () =>
    run(async () => {
      try {
        const a = await api.post<Agent>(`/api/agents/${encodeURIComponent(agent.id)}/${paused ? 'resume' : 'pause'}`)
        upsertAgent(a)
        onChange(a)
        toast(paused ? `${agent.name} снова в работе` : `${agent.name} на паузе — новые запуски не стартуют`, 'success')
      } catch (e) {
        toastError(e)
      }
    })
  return (
    <Button variant={paused ? 'primary' : 'secondary'} icon={paused ? 'play' : 'pause'} loading={busy} onClick={() => void toggle()}>
      {paused ? 'Возобновить' : 'Поставить на паузу'}
    </Button>
  )
}

function AskAgent({ agent }: { agent: Agent }) {
  const [q, setQ] = useState('')
  const [busy, run] = useBusy()
  const id = useId()
  const ask = () =>
    run(async () => {
      if (!q.trim()) return
      try {
        const t = await api.post<Task>(`/api/agents/${encodeURIComponent(agent.id)}/ask`, { question: q.trim() })
        setQ('')
        toast(`Вопрос отправлен: ${t.key}`, 'success')
        openTask(t.id)
      } catch (e) {
        toastError(e, 'Вопрос не отправлен')
      }
    })
  return (
    <Section title="Спросить агента">
      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          void ask()
        }}
      >
        <label htmlFor={id} className="sr-only">
          Вопрос агенту
        </label>
        <textarea
          id={id}
          className="ctl min-h-[70px]"
          placeholder={`Например: что сейчас с ${agent.service_ids[0] ?? 'твоими сервисами'}? Где риски?`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              void ask()
            }
          }}
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11.5px] text-subtle">Создаст задачу-вопрос сразу на агента</span>
          <Button type="submit" variant="primary" size="sm" icon="send" loading={busy} disabled={!q.trim()}>
            Спросить
          </Button>
        </div>
      </form>
    </Section>
  )
}

function AgentSettings({ agent, onSaved }: { agent: Agent; onSaved: (a: Agent) => void }) {
  const [mission, setMission] = useState(agent.mission)
  const [instructions, setInstructions] = useState(agent.instructions)
  const [model, setModel] = useState(agent.model)
  const [effort, setEffort] = useState(agent.effort ?? '')
  const [maxParallel, setMaxParallel] = useState(agent.max_parallel)
  const [title, setTitle] = useState(agent.title)
  const [busy, run] = useBusy()
  const f = { mission: useId(), instr: useId(), model: useId(), effort: useId(), par: useId(), title: useId() }

  const patch = useMemo(() => {
    const p: Partial<Agent> = {}
    if (mission !== agent.mission) p.mission = mission
    if (instructions !== agent.instructions) p.instructions = instructions
    if (model !== agent.model && model) p.model = model
    if ((effort || null) !== (agent.effort ?? null)) p.effort = effort || null
    if (maxParallel !== agent.max_parallel) p.max_parallel = maxParallel
    if (title !== agent.title) p.title = title
    return p
  }, [mission, instructions, model, effort, maxParallel, title, agent])
  const dirty = Object.keys(patch).length > 0

  useEffect(() => {
    if (!dirty) return
    const h = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', h)
    return () => window.removeEventListener('beforeunload', h)
  }, [dirty])

  const save = () =>
    run(async () => {
      try {
        const a = await api.patch<Agent>(`/api/agents/${encodeURIComponent(agent.id)}`, patch)
        upsertAgent(a)
        onSaved(a)
        toast('Настройки агента сохранены', 'success')
      } catch (e) {
        toastError(e, 'Не удалось сохранить')
      }
    })

  const reset = () => {
    setMission(agent.mission)
    setInstructions(agent.instructions)
    setModel(agent.model)
    setEffort(agent.effort ?? '')
    setMaxParallel(agent.max_parallel)
    setTitle(agent.title)
  }

  return (
    <Section
      title="Настройки"
      actions={
        dirty ? (
          <>
            <Button size="sm" variant="ghost" onClick={reset}>
              Сбросить
            </Button>
            <Button size="sm" variant="primary" loading={busy} onClick={() => void save()}>
              Сохранить
            </Button>
          </>
        ) : (
          <span className="text-[11.5px] text-subtle">сохранено</span>
        )
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Подпись" htmlFor={f.title}>
          <input id={f.title} className="ctl" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Модель Claude" hint="Codex использует соответствующий уровень раннера: Opus → усиленная модель, Sonnet/Haiku → основная." htmlFor={f.model}>
          <ModelInput id={f.model} value={model} onChange={setModel} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Усилие (effort)" htmlFor={f.effort}>
            <select id={f.effort} className="ctl" value={effort} onChange={(e) => setEffort(e.target.value)}>
              {EFFORTS.map((x) => (
                <option key={x} value={x}>
                  {x || 'по умолчанию'}
                </option>
              ))}
              {effort && !EFFORTS.includes(effort) ? <option value={effort}>{effort}</option> : null}
            </select>
          </Field>
          <Field label="Параллельно" htmlFor={f.par}>
            <input
              id={f.par}
              type="number"
              min={1}
              max={20}
              className="ctl"
              value={maxParallel}
              onChange={(e) => setMaxParallel(Math.max(1, Number(e.target.value) || 1))}
            />
          </Field>
        </div>
        <Field label="Миссия" htmlFor={f.mission}>
          <textarea id={f.mission} className="ctl min-h-[72px]" value={mission} onChange={(e) => setMission(e.target.value)} />
        </Field>
        <Field label="Инструкции" htmlFor={f.instr} hint="Попадают в системный промпт агента при каждом запуске.">
          <textarea
            id={f.instr}
            className="ctl min-h-[220px] font-mono text-[12px] leading-relaxed"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </Field>
      </div>
    </Section>
  )
}

function AgentTasks({ tasks }: { tasks: AgentDetail['tasks'] }) {
  const now = useNow()
  if (!tasks.length) return <Empty icon="list">Задач нет</Empty>
  const sorted = [...tasks].sort((a, b) => b.last_activity_at.localeCompare(a.last_activity_at))
  return (
    <ul className="divide-y divide-line rounded-card border border-line bg-surface">
      {sorted.map((t) => (
        <li key={t.id}>
          <TaskLink id={t.id} className="flex min-w-0 flex-wrap items-center gap-2 px-3 py-2 hover:bg-surface-2 sm:flex-nowrap">
            <StatusBadge status={t.status} />
            <span className="tnum shrink-0 font-mono text-[11.5px] text-subtle">{t.key}</span>
            {/* на телефоне название — отдельной строкой во всю ширину, а не 40 px между значками */}
            <span className={cx('min-w-0 flex-1 truncate max-sm:order-last max-sm:basis-full', t.status === 'done' && 'text-muted')}>{t.title}</span>
            {t.running ? <LiveDot label="работает" /> : null}
            <SubtaskProgress done={t.subtasks_done} total={t.subtasks_total} />
            <span className="tnum shrink-0 text-[11.5px] text-subtle max-sm:ml-auto">{timeAgo(t.last_activity_at, now)}</span>
          </TaskLink>
        </li>
      ))}
    </ul>
  )
}

/** Справочники сервисов агента и карта процессов его направления — если они есть. */
function AgentDocs({ agent }: { agent: Agent }) {
  const units = useDocUnits()
  const list = useMemo(() => {
    const all = units.data ?? []
    const own = unitsForServices(all, agent.service_ids)
    const proc = agent.direction_id ? all.filter((u) => u.kind === 'process' && u.unit === `process:${agent.direction_id}`) : []
    return [...own, ...proc]
  }, [units.data, agent.service_ids, agent.direction_id])
  if (!list.length) return null
  return (
    <Section title="Справочники">
      <ul className="flex flex-col gap-2">
        {list.map((u) => (
          <li key={u.unit}>
            <Link to={docHref(u.unit)} className="group flex items-start gap-2.5 rounded-card px-1 py-1 hover:bg-surface-2">
              <Icon name="book" size={16} className="mt-0.5 shrink-0 text-muted group-hover:text-accent" />
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 text-[13.5px] leading-snug font-medium text-fg group-hover:underline">
                  {u.title.replace(/\s+—\s+справочник\s*$/i, '')}
                </span>
                <span className="tnum mt-0.5 block text-[11.5px] break-words text-subtle">
                  {u.kind === 'process' ? 'процессы направления' : u.service_ids.join(', ') || u.unit} · {u.sections} разд. · {formatChars(u.chars)}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  )
}
